import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import {
  RiArrowRightLine,
  RiCloseLine,
  RiMailLine,
  RiLinkedinLine,
  RiTwitterLine,
  RiUserVoiceLine,
  RiGlobalLine,
} from "react-icons/ri";
import { Link } from "react-router-dom";
import { API_BASE } from "../config";
import { useSummit } from "../pages/summitdetails";

/* ─── THEME COLORS ───────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
};

/* ─── CSS ────────────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .speakers-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px);position:relative;overflow:hidden}
  .speakers-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .speakers-hero-i{max-width:1320px;margin:0 auto}
  .speakers-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .speakers-hero-h{font-family:'Playfair Display',serif;font-size:clamp(2rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .speakers-hero-h em{font-style:normal;color:${T.accentGold}}
  .speakers-hero-p{font-size:clamp(.88rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:560px}
`;

const getFullImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_BASE}${url.startsWith("/") ? url : "/" + url}`;
};

function SpeakerCard({ speaker, onClick }) {
  const [imgError, setImgError] = useState(false);
  const imageUrl = getFullImageUrl(speaker.imageUrl || speaker.image || speaker.photo);
  const speakerType = speaker.speakerType || speaker.type || "invited";
  const typeDisplay = speakerType.charAt(0).toUpperCase() + speakerType.slice(1);

  const getTypeColor = () => {
    switch (speakerType.toLowerCase()) {
      case "keynote": return T.accentGold;
      case "plenary": return T.primary;
      default: return T.accentTealLight;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      whileHover={{ y: -6 }}
      onClick={() => onClick(speaker)}
      style={{
        background: T.white,
        borderRadius: 20,
        overflow: "hidden",
        border: `1px solid ${T.borderLight}`,
        boxShadow: T.shadow,
        cursor: "pointer",
        transition: "all 0.3s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: 20,
      }}
    >
      {/* ── ROUND IMAGE ── */}
      <div
        style={{
          width: "80%",
          aspectRatio: "1/1",
          borderRadius: "50%",
          overflow: "hidden",
          background: T.gradSoft,
          border: `2px solid ${T.borderLight}`,
          position: "relative",
          marginBottom: 12,
        }}
      >
        {imageUrl && !imgError ? (
          <img
            src={imageUrl}
            alt={speaker.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            onError={() => setImgError(true)}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: T.gradSoft,
            }}
          >
            <span
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 48,
                fontWeight: 900,
                background: T.gradPrimary,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {speaker.name?.substring(0, 2).toUpperCase()}
            </span>
          </div>
        )}
        <span
          style={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: 10,
            fontWeight: 700,
            padding: "4px 12px",
            borderRadius: 20,
            background: getTypeColor() + "20",
            color: getTypeColor(),
            border: `1px solid ${getTypeColor()}40`,
            backdropFilter: "blur(4px)",
            whiteSpace: "nowrap",
          }}
        >
          {typeDisplay}
        </span>
      </div>

      {/* ── NAME & ROLE / UNIVERSITY ── */}
      <div style={{ padding: "0 16px 20px", textAlign: "center", width: "100%" }}>
        <h3
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 16,
            fontWeight: 800,
            color: T.text,
            marginBottom: 4,
          }}
        >
          {speaker.name}
        </h3>
        {speaker.role && (
          <p style={{ fontSize: 13, color: T.primary, fontWeight: 600, marginBottom: 2 }}>
            {speaker.role}
          </p>
        )}
        {speaker.company && (
          <p style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.4 }}>
            {speaker.company}
          </p>
        )}
      </div>
    </motion.div>
  );
}

/* ─── SPEAKER DETAIL MODAL – ROUND IMAGE LEFT ─────────────────── */
function SpeakerDetailModal({ speaker, onClose }) {
  const [imgError, setImgError] = useState(false);
  const imageUrl = getFullImageUrl(speaker.imageUrl || speaker.image || speaker.photo);
  const speakerType = speaker.speakerType || speaker.type || "invited";
  const typeDisplay = speakerType.charAt(0).toUpperCase() + speakerType.slice(1);

  const getTypeColor = () => {
    switch (speakerType.toLowerCase()) {
      case "keynote": return T.accentGold;
      case "plenary": return T.primary;
      default: return T.accentTealLight;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(8px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        style={{
          maxWidth: 800,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          background: T.white,
          borderRadius: 24,
          boxShadow: T.shadowLg,
          padding: 32,
          display: "flex",
          gap: 32,
          flexDirection: "row",
          alignItems: "flex-start",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── LEFT: ROUND IMAGE ── */}
        <div style={{ flexShrink: 0, width: 180, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div
            style={{
              width: "100%",
              aspectRatio: "1/1",
              borderRadius: "50%",
              overflow: "hidden",
              border: `2px solid ${T.borderLight}`,
              background: T.gradSoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {imageUrl && !imgError ? (
              <img
                src={imageUrl}
                alt={speaker.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={() => setImgError(true)}
              />
            ) : (
              <span
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 48,
                  fontWeight: 900,
                  background: T.gradPrimary,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {speaker.name?.substring(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          {/* Badge under image */}
          <div style={{ marginTop: 12, textAlign: "center" }}>
            <span
              style={{
                display: "inline-block",
                padding: "4px 16px",
                borderRadius: 30,
                fontSize: 11,
                fontWeight: 700,
                background: getTypeColor() + "15",
                color: getTypeColor(),
                border: `1px solid ${getTypeColor()}30`,
                letterSpacing: "0.04em",
              }}
            >
              {typeDisplay} Speaker
            </span>
          </div>
        </div>

        {/* ── RIGHT: CONTENT ── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name */}
          <h2
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 26,
              fontWeight: 800,
              color: T.text,
              marginBottom: 2,
            }}
          >
            {speaker.name}
          </h2>
          {/* Role */}
          {speaker.role && (
            <p style={{ fontSize: 15, color: T.primary, fontWeight: 600, marginBottom: 2 }}>
              {speaker.role}
            </p>
          )}
          {/* Institution / University */}
          {speaker.company && (
            <p style={{ fontSize: 14, color: T.textMid, marginBottom: 16 }}>
              <span style={{ fontWeight: 500 }}>🏛️</span> {speaker.company}
            </p>
          )}

          {/* Biography */}
          {speaker.bio && (
            <div style={{ marginBottom: 16 }}>
              <h4
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: T.text,
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Biography
              </h4>
              <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.7 }}>
                {speaker.bio}
              </p>
            </div>
          )}

          {/* Expertise */}
          {speaker.expertise && speaker.expertise.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: T.text,
                  marginBottom: 6,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Areas of Expertise
              </h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {speaker.expertise.map((exp, idx) => (
                  <span
                    key={idx}
                    style={{
                      padding: "4px 12px",
                      borderRadius: 16,
                      fontSize: 12,
                      background: `${T.primary}15`,
                      color: T.primary,
                      border: `1px solid ${T.primary}30`,
                    }}
                  >
                    {exp}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Key Achievements */}
          {speaker.achievements && speaker.achievements.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: T.text,
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Key Achievements
              </h4>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {speaker.achievements.map((ach, idx) => (
                  <li key={idx} style={{ fontSize: 14, color: T.textMid, marginBottom: 4 }}>
                    {ach}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Key Publications */}
          {speaker.publications && speaker.publications.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: T.text,
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Key Publications
              </h4>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {speaker.publications.map((pub, idx) => (
                  <li key={idx} style={{ fontSize: 14, color: T.textMid, marginBottom: 4 }}>
                    {pub}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Social links */}
          <div
            style={{
              display: "flex",
              gap: 16,
              paddingTop: 16,
              borderTop: `1px solid ${T.borderLight}`,
              flexWrap: "wrap",
            }}
          >
            {speaker.email && (
              <a
                href={`mailto:${speaker.email}`}
                style={{
                  color: T.primary,
                  fontSize: 13,
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = T.accentGold; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = T.primary; }}
              >
                <RiMailLine size={18} /> Email
              </a>
            )}
            {speaker.linkedinUrl && (
              <a
                href={speaker.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: T.primary,
                  fontSize: 13,
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = T.accentGold; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = T.primary; }}
              >
                <RiLinkedinLine size={18} /> LinkedIn
              </a>
            )}
            {speaker.twitterUrl && (
              <a
                href={speaker.twitterUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: T.primary,
                  fontSize: 13,
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = T.accentGold; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = T.primary; }}
              >
                <RiTwitterLine size={18} /> Twitter
              </a>
            )}
            {speaker.website && (
              <a
                href={speaker.website}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: T.primary,
                  fontSize: 13,
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = T.accentGold; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = T.primary; }}
              >
                <RiGlobalLine size={18} /> Website
              </a>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              marginTop: 20,
              padding: "8px 24px",
              borderRadius: 30,
              border: `1px solid ${T.border}`,
              background: T.white,
              color: T.textMid,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 500,
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = T.primary;
              e.currentTarget.style.color = T.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = T.border;
              e.currentTarget.style.color = T.textMid;
            }}
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── MAIN SPEAKERS COMPONENT ───────────────────────────────────── */
export default function Speakers() {
  const { eventId } = useSummit();
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("plenary");
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);

  useEffect(() => {
    if (eventId) fetchSpeakers();
    else setLoading(false);
  }, [eventId]);

  const fetchSpeakers = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/summit-speakers/event/${eventId}`);
      let speakersData = [];
      if (data.success) {
        speakersData = data.speakers || data.data || data.results || [];
        if (Array.isArray(data)) speakersData = data;
      }
      const processed = speakersData.map((s) => ({
        ...s,
        speakerType: s.speakerType || s.type || "invited",
        expertise: s.expertise || [],
        achievements: s.achievements || [],
        publications: s.publications || [],
        linkedinUrl: s.linkedinUrl || s.linkedin || "",
        twitterUrl: s.twitterUrl || s.twitter || "",
      }));
      setSpeakers(processed);
    } catch (error) {
      console.error("Error fetching speakers:", error);
      toast.error("Failed to load speakers");
      setSpeakers([]);
    } finally {
      setLoading(false);
    }
  };

  const tabOrder = ["plenary", "keynote", "invited"];
  const tabLabels = {
    plenary: "Plenary",
    keynote: "Keynote",
    invited: "Invited",
  };

  const filteredSpeakers = speakers.filter(
    (s) => (s.speakerType || s.type || "invited") === activeTab
  );

  const counts = {
    plenary: speakers.filter((s) => (s.speakerType || s.type || "invited") === "plenary").length,
    keynote: speakers.filter((s) => (s.speakerType || s.type || "invited") === "keynote").length,
    invited: speakers.filter((s) => (s.speakerType || s.type || "invited") === "invited").length,
  };

  if (!eventId) {
    return (
      <div style={{ minHeight: "100vh", background: T.offWhite, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎤</div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>No Event Selected</h3>
          <p style={{ color: T.textMuted }}>Please select an event to view its speakers.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div style={{ minHeight: "100vh", background: T.offWhite }}>
        <Toaster position="top-right" />

        <section className="speakers-hero">
          <div className="speakers-hero-i">
            <div>
              <div className="speakers-hero-lbl">
                <RiUserVoiceLine size={13} />
                Academic Summit 2026
              </div>
              <h1 className="speakers-hero-h">
                Our <em>Speakers</em>
              </h1>
              <p className="speakers-hero-p">
                World-renowned academics, researchers, and pioneers gathered from around the globe
                to share insights and expertise across immunology and related fields.
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 24 }}>
                <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
                <div style={{ width: 8, height: 8, background: T.accentGold, transform: "rotate(45deg)" }} />
                <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              </div>
            </div>
          </div>
        </section>

        <div style={{ maxWidth: 1320, margin: "0 auto", padding: "40px 40px 0" }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 32 }}>
            {tabOrder.map((tab) => (
              <div
                key={tab}
                style={{
                  background: T.white,
                  borderRadius: 16,
                  padding: "20px 28px",
                  border: `1px solid ${activeTab === tab ? T.primary : T.border}`,
                  flex: 1,
                  minWidth: 120,
                  transition: "all 0.3s",
                  boxShadow: T.shadow,
                  cursor: "pointer",
                }}
                onClick={() => setActiveTab(tab)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = T.shadowMd;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = T.shadow;
                }}
              >
                <div style={{ fontSize: 32, fontWeight: 800, color: activeTab === tab ? T.primary : T.text }}>
                  {counts[tab]}
                </div>
                <div style={{ fontSize: 13, color: T.textMuted }}>{tabLabels[tab]} Speakers</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ maxWidth: 1320, margin: "0 auto", padding: "0 40px 80px" }}>
          {loading ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 28 }}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} style={{ background: T.offWhite, borderRadius: 20, border: `1px solid ${T.border}`, height: 380 }} />
              ))}
            </div>
          ) : filteredSpeakers.length === 0 ? (
            <div style={{ textAlign: "center", padding: 80, background: T.white, borderRadius: 24, border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🎤</div>
              <p style={{ color: T.textMuted, fontSize: 16 }}>No speakers found for {tabLabels[activeTab]} category.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 28 }}>
              {filteredSpeakers.map((speaker, idx) => (
                <SpeakerCard key={speaker._id || idx} speaker={speaker} onClick={setSelectedSpeaker} />
              ))}
            </div>
          )}
        </div>

        <div style={{ maxWidth: 1320, margin: "0 auto", padding: "0 40px 80px" }}>
          <div
            style={{
              background: T.gradNavy,
              borderRadius: 24,
              padding: "48px 56px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 32,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ position: "absolute", width: 300, height: 300, background: "radial-gradient(circle, rgba(212,160,23,0.1), transparent)", borderRadius: "50%", top: -100, right: -80, pointerEvents: "none" }} />
            <div>
              <div style={{ display: "inline-block", padding: "4px 12px", borderRadius: 20, background: "rgba(212,160,23,0.15)", border: "1px solid rgba(212,160,23,0.25)", color: T.accentGoldLight, fontSize: 11, fontWeight: 700, marginBottom: 12 }}>
                Become a Speaker
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.4rem, 2.5vw, 2rem)", fontWeight: 900, color: T.white, marginBottom: 8 }}>
                Share Your Expertise
              </h2>
              <p style={{ fontSize: 14, color: T.textMuted }}>Submit your abstract or proposal to join our distinguished speaker lineup.</p>
            </div>
            <div>
              <Link
                to={`/abstract-submission${eventId ? `?eventId=${eventId}` : ""}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "13px 32px",
                  background: T.gradGold,
                  color: T.navyDeep,
                  borderRadius: 50,
                  textDecoration: "none",
                  fontSize: 14,
                  fontWeight: 700,
                  transition: "all 0.25s",
                  boxShadow: T.shadowGold,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = T.shadowLg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = T.shadowGold;
                }}
              >
                Submit Proposal
                <RiArrowRightLine size={15} />
              </Link>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {selectedSpeaker && (
            <SpeakerDetailModal speaker={selectedSpeaker} onClose={() => setSelectedSpeaker(null)} />
          )}
        </AnimatePresence>
      </div>
    </>
  );
}